/**
 * VehicleReceivedModal
 *
 * Collects loan car handover data before marking a booking as VEHICLE_RECEIVED:
 *   - Odometer reading
 *   - Insurance taken toggle
 *   - Handover notes
 *   - Photos per category (file picker / camera → uploaded to S3 immediately on select)
 *
 * Upload flow:
 *   1. Staff taps "Add photo" or "Take photo" in a category section.
 *   2. Browser file picker opens (accept="image/*", capture="environment" for camera).
 *   3. On file select → POST /loan-car-incidents/upload-photo?category=... (multipart).
 *   4. On success → s3Key is stored in state; thumbnail shown from local object URL.
 *   5. On submit → s3Keys are sent as part of the PATCH /vehicle-received payload.
 *
 * Mandatory rules (mirror backend):
 *   DRIVING_LICENCE                → always required (≥ 1 photo)
 *   CAR_DASHBOARD_ON_VEHICLE_START → always required (≥ 1 photo)
 *   INSURANCE_CERTIFICATE          → required when insuranceTaken = true
 *   OTHER                          → optional
 */

import React, { useState, useMemo, useRef, useCallback } from 'react';
import {
    X, Car, FileText, Image, Plus, Trash2, Loader2,
    AlertCircle, CheckCircle, Info, Gauge, Shield, CreditCard,
    LayoutDashboard, Paperclip, Camera, Upload, Fuel
} from 'lucide-react';
import { uploadLoanCarPhotoApi } from '../services/loanCarIncidentApi';
import { FUEL_OPTIONS, parseFuelFromNotes, applyFuelToNotes } from './fuelLevel';

// ── Fallback categories ─────────────────────────────────────────────────────
const FALLBACK_CATEGORIES = [
    { category: 'DRIVING_LICENCE',                label: 'Driving Licence',          description: "Photo of the customer's driving licence",                                        mandatory: true  },
    { category: 'CAR_DASHBOARD_ON_VEHICLE_START', label: 'Dashboard on Vehicle Start',description: 'Dashboard photo taken when the vehicle is started at handover',                 mandatory: true  },
    { category: 'INSURANCE_CERTIFICATE',          label: 'Insurance Certificate',     description: 'Proof of supplementary insurance — required when insurance is taken',            mandatory: false },
    { category: 'OTHER',                          label: 'Other',                     description: 'Additional photos: existing damage, fuel gauge, general condition',              mandatory: false },
];

// ── Category icon ───────────────────────────────────────────────────────────
const CategoryIcon = ({ category, size = 15, className = '' }) => {
    switch (category) {
        case 'DRIVING_LICENCE':             return <CreditCard size={size} className={className} />;
        case 'CAR_DASHBOARD_ON_VEHICLE_START': return <LayoutDashboard size={size} className={className} />;
        case 'INSURANCE_CERTIFICATE':       return <Shield size={size} className={className} />;
        default:                            return <Paperclip size={size} className={className} />;
    }
};

// ── Label ───────────────────────────────────────────────────────────────────
const Label = ({ children, required }) => (
    <label className="block text-sm font-medium text-gray-700 mb-1">
        {children}
        {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
);

// ── Single uploaded photo thumbnail ────────────────────────────────────────
const PhotoThumb = ({ photo, onRemove }) => (
    <div className="relative group">
        {photo.uploading ? (
            <div className="w-16 h-16 rounded-lg border-2 border-dashed border-emerald-300 bg-emerald-50 flex flex-col items-center justify-center gap-1">
                <Loader2 size={16} className="animate-spin text-emerald-500" />
                <span className="text-[10px] text-emerald-600 font-medium">
                    {photo.progress != null ? `${photo.progress}%` : '…'}
                </span>
            </div>
        ) : photo.error ? (
            <div className="w-16 h-16 rounded-lg border-2 border-red-200 bg-red-50 flex flex-col items-center justify-center gap-1 p-1">
                <AlertCircle size={14} className="text-red-400" />
                <span className="text-[9px] text-red-500 text-center leading-tight">Upload failed</span>
            </div>
        ) : (
            <img
                src={photo.previewUrl}
                alt="handover photo"
                className="w-16 h-16 rounded-lg object-cover border border-gray-200 shadow-sm"
            />
        )}
        {/* Remove button — shown on hover for uploaded, always for error */}
        {!photo.uploading && (
            <button
                type="button"
                onClick={() => onRemove(photo.id)}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow"
                title="Remove photo"
            >
                <X size={10} />
            </button>
        )}
    </div>
);

// ── Per-category image section ──────────────────────────────────────────────
const CategorySection = ({ catDef, photos, isRequired, submitted, onFilesSelected }) => {
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);

    const missingRequired = submitted && isRequired && photos.filter(p => p.s3Key).length === 0;
    const uploadedCount   = photos.filter(p => p.s3Key).length;
    const uploadingCount  = photos.filter(p => p.uploading).length;
    const complete        = isRequired ? uploadedCount >= 1 : true;

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length > 0) onFilesSelected(files);
        // Reset so the same file can be re-selected if needed
        e.target.value = '';
    };

    return (
        <div className={`rounded-xl border p-4 space-y-3 transition-colors ${
            missingRequired
                ? 'border-red-300 bg-red-50/30'
                : complete && uploadedCount > 0
                    ? 'border-emerald-200 bg-emerald-50/20'
                    : 'border-gray-200 bg-gray-50/40'
        }`}>
            {/* Header */}
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2">
                    <CategoryIcon
                        category={catDef.category}
                        size={15}
                        className={`mt-0.5 shrink-0 ${
                            missingRequired ? 'text-red-500' : complete && uploadedCount > 0 ? 'text-emerald-600' : 'text-gray-400'
                        }`}
                    />
                    <div>
                        <p className="text-sm font-semibold text-gray-800 flex items-center gap-1.5 flex-wrap">
                            {catDef.label}
                            {isRequired ? (
                                <span className="text-[10px] font-medium text-white bg-red-400 rounded px-1.5 py-0.5 leading-none">Required</span>
                            ) : (
                                <span className="text-[10px] font-medium text-gray-500 bg-gray-200 rounded px-1.5 py-0.5 leading-none">Optional</span>
                            )}
                            {uploadedCount > 0 && (
                                <span className="text-[10px] font-medium text-emerald-700 bg-emerald-100 rounded px-1.5 py-0.5 leading-none">
                                    {uploadedCount} photo{uploadedCount !== 1 ? 's' : ''}
                                </span>
                            )}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">{catDef.description}</p>
                    </div>
                </div>
                {complete && uploadedCount > 0 && !uploadingCount && (
                    <CheckCircle size={15} className="text-emerald-500 shrink-0 mt-0.5" />
                )}
            </div>

            {/* Error banner */}
            {missingRequired && (
                <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle size={12} /> At least one photo is required for {catDef.label}
                </p>
            )}

            {/* Thumbnails */}
            {photos.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {photos.map(photo => (
                        <PhotoThumb key={photo.id} photo={photo} onRemove={() => {}} />
                    ))}
                </div>
            )}

            {/* Hidden file inputs */}
            {/* Gallery / file picker */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleFileChange}
            />
            {/* Camera (mobile) */}
            <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleFileChange}
            />

            {/* Action buttons */}
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={() => cameraInputRef.current?.click()}
                    className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 text-sm sm:text-xs text-emerald-600 hover:text-emerald-700 font-medium transition-colors bg-emerald-50 hover:bg-emerald-100 px-3 sm:px-2.5 py-2.5 sm:py-1.5 min-h-[44px] sm:min-h-0 rounded-lg"
                >
                    <Camera size={15} className="sm:hidden" /><Camera size={13} className="hidden sm:block" /> Take photo
                </button>
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 text-sm sm:text-xs text-gray-600 hover:text-gray-700 font-medium transition-colors bg-gray-100 hover:bg-gray-200 px-3 sm:px-2.5 py-2.5 sm:py-1.5 min-h-[44px] sm:min-h-0 rounded-lg"
                >
                    <Upload size={15} className="sm:hidden" /><Upload size={13} className="hidden sm:block" /> Attach file
                </button>
            </div>
        </div>
    );
};

// ── Main modal ──────────────────────────────────────────────────────────────
const VehicleReceivedModal = ({ booking, imageCategories, onConfirm, onClose, loading, error }) => {

    const resolvedCategories = useMemo(
        () => (imageCategories && imageCategories.length > 0 ? imageCategories : FALLBACK_CATEGORIES),
        [imageCategories]
    );

    const [odometerReading, setOdometerReading] = useState('');
    const [insuranceTaken, setInsuranceTaken]   = useState(false);
    const [notes, setNotes]                     = useState('');
    const [submitted, setSubmitted]             = useState(false);

    // photosByCategory: { [category]: [{ id, file, previewUrl, s3Key, uploading, progress, error }] }
    const [photosByCategory, setPhotosByCategory] = useState(
        () => Object.fromEntries(resolvedCategories.map(c => [c.category, []]))
    );

    const isRequired = (catDef) => {
        if (catDef.category === 'INSURANCE_CERTIFICATE') return insuranceTaken;
        return catDef.mandatory;
    };

    // ── Upload handler ──────────────────────────────────────────────────────
    const handleFilesSelected = useCallback(async (category, files) => {
        for (const file of files) {
            const id         = `${Date.now()}-${Math.random()}`;
            const previewUrl = URL.createObjectURL(file);

            // Add placeholder with uploading state
            setPhotosByCategory(prev => ({
                ...prev,
                [category]: [...(prev[category] || []), { id, file, previewUrl, s3Key: null, uploading: true, progress: 0, error: null }],
            }));

            try {
                const { s3Key } = await uploadLoanCarPhotoApi(
                    file,
                    category,
                    (progress) => setPhotosByCategory(prev => ({
                        ...prev,
                        [category]: (prev[category] || []).map(p =>
                            p.id === id ? { ...p, progress } : p
                        ),
                    }))
                );

                // Mark as uploaded
                setPhotosByCategory(prev => ({
                    ...prev,
                    [category]: (prev[category] || []).map(p =>
                        p.id === id ? { ...p, s3Key, uploading: false, progress: 100 } : p
                    ),
                }));

            } catch (err) {
                setPhotosByCategory(prev => ({
                    ...prev,
                    [category]: (prev[category] || []).map(p =>
                        p.id === id ? { ...p, uploading: false, error: 'Upload failed' } : p
                    ),
                }));
            }
        }
    }, []);

    const removePhoto = useCallback((category, id) => {
        setPhotosByCategory(prev => ({
            ...prev,
            [category]: (prev[category] || []).filter(p => p.id !== id),
        }));
    }, []);

    // ── Validation ──────────────────────────────────────────────────────────
    const anyUploading = resolvedCategories.some(c =>
        (photosByCategory[c.category] || []).some(p => p.uploading)
    );

    const validate = () => {
        const missingRequired = resolvedCategories
            .filter(isRequired)
            .some(c => (photosByCategory[c.category] || []).filter(p => p.s3Key).length === 0);
        const hasUploadErrors = resolvedCategories.some(c =>
            (photosByCategory[c.category] || []).some(p => p.error)
        );
        return !missingRequired && !hasUploadErrors;
    };

    const totalUploaded = resolvedCategories.reduce(
        (sum, c) => sum + (photosByCategory[c.category] || []).filter(p => p.s3Key).length, 0
    );
    const allRequiredMet = resolvedCategories
        .filter(isRequired)
        .every(c => (photosByCategory[c.category] || []).filter(p => p.s3Key).length >= 1);

    // ── Submit ──────────────────────────────────────────────────────────────
    const handleSubmit = () => {
        setSubmitted(true);
        if (!validate() || anyUploading) return;

        const imagesByCategoryPayload = resolvedCategories
            .map(c => ({
                category: c.category,
                images: (photosByCategory[c.category] || [])
                    .filter(p => p.s3Key)
                    .map(p => ({ s3Key: p.s3Key })),
            }))
            .filter(entry => entry.images.length > 0);

        onConfirm({
            loanCarDetails: {
                odometerReading: odometerReading !== '' ? parseInt(odometerReading, 10) : null,
                insuranceTaken,
                notes: notes.trim() || null,
                imagesByCategory: imagesByCategoryPayload,
            },
        });
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-0 sm:p-4">
            <div className="bg-white rounded-none sm:rounded-2xl w-full max-w-3xl h-full sm:h-auto
                            sm:max-h-[92vh] flex flex-col shadow-2xl">

                {/* ── Header ── */}
                <div className="flex items-start justify-between p-5 pb-4 border-b border-gray-100 shrink-0">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center shrink-0">
                            <Car size={20} className="text-emerald-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Vehicle Received</h2>
                            <p className="text-sm text-gray-500 mt-0.5">
                                {booking.bookingCode} · {booking.customerName}
                            </p>
                        </div>
                    </div>
                    <button type="button" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* ── Notice ── */}
                <div className="mx-5 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2.5 shrink-0">
                    <Info size={15} className="text-amber-600 mt-0.5 shrink-0" />
                    <div>
                        <p className="text-sm font-semibold text-amber-800">Loan car handover checklist</p>
                        <p className="text-xs text-amber-600 mt-0.5">
                            Driving licence and dashboard photos are always required.
                            Insurance certificate required if insurance was taken.
                        </p>
                    </div>
                </div>

                {/* ── Body ── */}
                <div className="overflow-y-auto flex-1 p-5 space-y-5">

                    {error && (
                        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                            <AlertCircle size={15} className="shrink-0 mt-0.5" /> {error}
                        </div>
                    )}

                    {/* Odometer + Insurance */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <Label><span className="flex items-center gap-1.5"><Gauge size={14} />Odometer (km)</span></Label>
                            <input
                                type="number" min="0" placeholder="e.g. 45230"
                                value={odometerReading}
                                onChange={(e) => setOdometerReading(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            />
                        </div>
                        <div>
                            <Label><span className="flex items-center gap-1.5"><Shield size={14} />Insurance Taken</span></Label>
                            <div className="flex items-center gap-2 mt-1.5">
                                <button type="button" onClick={() => setInsuranceTaken(false)}
                                        className={`flex-1 py-2 min-h-[44px] sm:min-h-0 rounded-lg text-sm font-medium border transition-colors ${!insuranceTaken ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                                    No
                                </button>
                                <button type="button" onClick={() => setInsuranceTaken(true)}
                                        className={`flex-1 py-2 min-h-[44px] sm:min-h-0 rounded-lg text-sm font-medium border transition-colors ${insuranceTaken ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                                    Yes
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Handover Notes */}
                    <div>
                        <Label><span className="flex items-center gap-1.5"><FileText size={14} />Handover Notes</span></Label>

                        {/* Fuel level quick-pick — writes a canonical
                            "Fuel: <label>" line at the top of the notes.
                            Single-select: clicking another option swaps the
                            line, clicking the active option again clears it.
                            The active state is parsed from the notes string
                            on each render, so manual edits in the textarea
                            remain authoritative. Helpers are shared via
                            ./fuelLevel. */}
                        {(() => {
                            const activeFuel = parseFuelFromNotes(notes);
                            return (
                                <div className="flex items-center flex-wrap gap-1.5 mb-2">
                                    <span className="text-xs text-gray-500 inline-flex items-center gap-1 mr-0.5">
                                        <Fuel size={12} /> Fuel level
                                    </span>
                                    {FUEL_OPTIONS.map(opt => {
                                        const active = activeFuel === opt.key;
                                        return (
                                            <button
                                                key={opt.key}
                                                type="button"
                                                onClick={() => setNotes(applyFuelToNotes(notes, active ? null : opt.key))}
                                                aria-pressed={active}
                                                className={
                                                    'px-2.5 py-0.5 rounded-full text-xs border transition-colors ' +
                                                    (active
                                                        ? 'bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700'
                                                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50')
                                                }
                                            >
                                                {opt.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            );
                        })()}

                        <textarea
                            value={notes} onChange={(e) => setNotes(e.target.value)}
                            rows={3} placeholder="Existing scratches, damage or known issues…"
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                        />
                        <p className="text-xs text-gray-400 mt-0.5">{notes.length} / 5000 characters</p>
                    </div>

                    {/* Photo categories */}
                    <div className="space-y-3">
                        <div className="flex items-center gap-1.5">
                            <Image size={14} className="text-gray-500" />
                            <p className="text-sm font-medium text-gray-700">
                                Condition Photos
                                <span className="text-gray-400 font-normal ml-1.5">
                                    ({totalUploaded} uploaded{anyUploading ? ', uploading…' : ''})
                                </span>
                            </p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {resolvedCategories.map((catDef) => {
                                if (catDef.category === 'INSURANCE_CERTIFICATE' && !insuranceTaken) {
                                    return null;
                                }
                                return (
                                    <CategorySection
                                        key={catDef.category}
                                        catDef={catDef}
                                        photos={photosByCategory[catDef.category] || []}
                                        isRequired={isRequired(catDef)}
                                        submitted={submitted}
                                        onFilesSelected={(files) => handleFilesSelected(catDef.category, files)}
                                        onRemove={(id) => removePhoto(catDef.category, id)}
                                    />
                                );
                            })}
                        </div>
                    </div>

                    {/* Readiness indicator */}
                    <div className={`flex items-center gap-2 p-3 rounded-xl text-sm font-medium ${
                        anyUploading
                            ? 'bg-blue-50 border border-blue-200 text-blue-700'
                            : allRequiredMet
                                ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                                : 'bg-gray-50 border border-gray-200 text-gray-500'
                    }`}>
                        {anyUploading
                            ? <Loader2 size={15} className="shrink-0 animate-spin" />
                            : allRequiredMet
                                ? <CheckCircle size={15} className="shrink-0" />
                                : <Info size={15} className="shrink-0" />
                        }
                        {anyUploading
                            ? 'Uploading photos — please wait…'
                            : allRequiredMet
                                ? 'All required photos provided — ready to submit'
                                : 'Required photos are still missing'
                        }
                    </div>
                </div>

                {/* ── Footer ── */}
                <div className="p-5 pt-0 pb-6 sm:pb-5 shrink-0 flex gap-3 border-t border-gray-100 mt-2">
                    <button type="button" onClick={onClose} disabled={loading}
                            className="px-5 py-2.5 min-h-[48px] sm:min-h-0 border border-gray-200 rounded-xl text-gray-700 font-medium text-sm hover:bg-gray-50 transition-colors disabled:opacity-50">
                        Cancel
                    </button>
                    <button type="button" onClick={handleSubmit} disabled={loading || anyUploading}
                            className="flex-1 py-2.5 min-h-[48px] sm:min-h-0 bg-emerald-600 text-white rounded-xl font-medium text-sm hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                        {(loading || anyUploading) && <Loader2 size={16} className="animate-spin" />}
                        <Car size={15} />
                        {anyUploading ? 'Uploading…' : 'Mark Vehicle Received'}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default VehicleReceivedModal;